import { Box, Stack, TextField, Typography } from '@mui/material'
import type { FormikProps } from 'formik'
import { NumberedSectionCard } from '~/shared/ui'
import {
  ACCUMULATOR_TIERS,
  type AccumulatorValueField,
  type PolicyEditorValues,
} from './policyEditorForm'

interface AccumulatorsSectionProps {
  formik: FormikProps<PolicyEditorValues>
  readOnly: boolean
}

const COLUMNS = [
  { side: 'individual', suffix: 'deductible', label: 'Individual deductible' },
  { side: 'individual', suffix: 'out_of_pocket', label: 'Individual OOP max' },
  { side: 'family', suffix: 'deductible', label: 'Family deductible' },
  { side: 'family', suffix: 'out_of_pocket', label: 'Family OOP max' },
] as const

/**
 * Section 2: the deductible and out-of-pocket maximum for each network tier.
 *
 * Twelve values across two `PolicyAccumulator` rows. Publishing needs all twelve, but a draft is
 * allowed to hold any subset — which is the point of drafts, so nothing here is marked required.
 */
export const AccumulatorsSection = ({ formik, readOnly }: AccumulatorsSectionProps) => (
  <NumberedSectionCard
    index={2}
    title="Accumulators"
    hint="Deductible and out-of-pocket maximum per network tier. All twelve are required to publish."
    testId="section-accumulators"
  >
    <Stack spacing={2}>
      {ACCUMULATOR_TIERS.map(tier => (
        <Box key={tier.key}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {tier.label}
          </Typography>
          <Stack direction="row" spacing={2}>
            {COLUMNS.map(column => {
              const field: AccumulatorValueField = `${tier.key}_${column.suffix}`
              return (
                <TextField
                  key={column.label}
                  fullWidth
                  type="number"
                  name={`${column.side}.${field}`}
                  label={column.label}
                  value={formik.values[column.side][field]}
                  // Not `handleChange`: it coerces a number input with `parseFloat`, which would
                  // turn "1500.00" into 1500 and make an untouched field read as edited.
                  onChange={event =>
                    void formik.setFieldValue(`${column.side}.${field}`, event.target.value)
                  }
                  disabled={readOnly}
                  data-testid={`editor-${column.side}-${field}`}
                />
              )
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  </NumberedSectionCard>
)
